import React, { useEffect, useState, useContext } from "react";
// Import the custom Axios instance instead of default axios
import axiosInstance from "utils/axiosInstance";
import { AuthContext } from "context/AuthContext"; // Import AuthContext
import axios from "axios";
import Card from "components/card";
import Dropdown from "components/dropdown";
import { AiOutlineUser } from "react-icons/ai";
import { FaPlus } from "react-icons/fa6";
import { toast } from "react-toastify";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
  getPaginationRowModel,
} from "@tanstack/react-table";
import Modal from 'components/Modal'

import { MdEdit } from "react-icons/md";
import { IoTrash } from "react-icons/io5";
import { format } from "date-fns";
import {
  Button,
  EmptyState,
  Pagination,
  SortIndicator,
  TextField,
  tdClass,
  tdStrongClass,
  thClass,
  thLabelClass,
  theadRowClass,
  trClass,
} from "components/ui";

// ====== TYPES ======
type RowObj = {
  id: number;
  name: [string, boolean]; // e.g. ["Juan", true]
  createdAt: string;       // from DB
  clasificacion: string;   // "Copa Libertadores" | "Copa Sudamericana" | "Descenso"
  baseCalculo: string;     // e.g. "Total" or "5"
  quantity: number;        // # of Fútbol events
  penas: number;           // # of Peñas events
  date: string;            // not super crucial here
};

/* ===================== HELPER FOR DATES ===================== */
const formatDate = (isoDate: string) => format(new Date(isoDate), "dd/MM/yy");

function Jugadores() {
  // Access auth state from context
  const { isLoggedIn } = useContext(AuthContext);

  // ====== TABLE / SORTING / DATA ======
  const [sorting, setSorting] = useState<SortingState>([]);
  const [data, setData] = useState<RowObj[]>([]);

  // ====== MODAL STATES ======
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [nuevoJugador, setNuevoJugador] = useState("");

  // EDIT
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editPersona, setEditPersona] = useState<RowObj | null>(null);
  const [nombre, setNombre] = useState("");

  // DELETE
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletePersonaId, setDeletePersonaId] = useState<number | null>(null);

  // PAGINATION
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  // ====== FETCH / TRANSFORM DATA ======
  const fetchPersonData = async () => {
    try {
      const [resumenResponse, penasResponse, futbolResponse, eventosResponse] =
        await Promise.all([
          axiosInstance.get("/eventos/resumen"),
          axiosInstance.get("/eventos/penas-resumen"),
          axiosInstance.get("/eventos/futbol-resumen"),
          axiosInstance.get("/eventos"),
        ]);

      const thisYear = new Date().getFullYear();
      const allEventsThisYear = eventosResponse.data.filter(
        (evt: any) => new Date(evt.fecha).getFullYear() === thisYear
      );
      const totalEvents = allEventsThisYear.length;

      // Build quick-lookup maps
      const penasMap = penasResponse.data.reduce(
        (map: Record<number, number>, item: any) => {
          map[item.id] = item.penas;
          return map;
        },
        {}
      );
      const futbolMap = futbolResponse.data.reduce(
        (map: Record<number, number>, item: any) => {
          map[item.id] = item.futbol;
          return map;
        },
        {}
      );

      // Merge data
      const transformedData: RowObj[] = resumenResponse.data.map((persona: any) => {

        const createdDay = new Date(persona.createdAt);
        createdDay.setHours(0, 0, 0, 0);
        const eventsSinceJoined = allEventsThisYear.filter((evt: any) => {
          const eventDay = new Date(evt.fecha);
          eventDay.setHours(0, 0, 0, 0);
          return eventDay >= createdDay;
        });
        const partialCount = eventsSinceJoined.length;

        let baseCalculo = "Total";
        if (partialCount < totalEvents) {
          baseCalculo = `${partialCount}`;
        }

        return {
          id: persona.id,
          name: [persona.nombre, true],
          createdAt: persona.createdAt,
          baseCalculo,
          quantity: futbolMap[persona.id] || 0,
          penas: penasMap[persona.id] || 0,
          date: persona.ultimaAsistencia 
          ? formatDate(persona.ultimaAsistencia)
          : "-", 
        };
      });

      setData(transformedData);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  useEffect(() => {
    fetchPersonData();
  }, []);

  // ====== DROPDOWN BUTTON (CREAR JUGADOR) ======
  function AgregarJugador(props: { transparent?: boolean }) {
    const { transparent } = props;
    const [open, setOpen] = React.useState(false);

    return (
      <Dropdown
        button={
          <Button
            variant={transparent ? "ghost" : "soft"}
            size="icon"
            aria-label="Agregar jugador"
            onClick={() => setOpen(!open)}
          >
            <FaPlus className="h-4 w-4" />
          </Button>
        }
        classNames={`${transparent ? "top-8" : "top-11"} right-0 w-max`}
      >
        <div className="z-50 w-max select-none overflow-hidden rounded-card border border-gray-200/80 bg-white p-1.5 shadow-pop dark:border-white/10 dark:!bg-navy-700">
          <button
            type="button"
            className="flex w-full cursor-pointer items-center gap-2.5 rounded-control px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-white/5 dark:hover:text-white"
            onClick={() => {
              setIsCreateModalOpen(true);
              setNuevoJugador("");
            }}
          >
            <AiOutlineUser className="h-[18px] w-[18px] shrink-0 text-gray-600 dark:text-gray-500" />
            Agregar Jugador
          </button>
        </div>
      </Dropdown>
    );
  }

  // ========== CREATE JUGADOR ==========
  const handleCrearJugador = async () => {
    if (!nuevoJugador.trim()) {
      toast.error("El nombre del jugador es obligatorio.");
      return;
    }
    const duplicate = data.some(
      (row) =>
        row.name[0].trim().toLowerCase() === nuevoJugador.trim().toLowerCase()
    );
    if (duplicate) {
      toast.error("Ya existe un jugador con ese nombre.");
      return;
    }

    try {
      await axiosInstance.post("/personas", {
        nombre: nuevoJugador,
      });
      setNuevoJugador("");
      setIsCreateModalOpen(false);
      // re-fetch to show new player
      fetchPersonData();
    } catch (error: any) {
      console.error("Error al crear jugador:", error);
      if (axios.isAxiosError(error) && error.response) {
        if (
          error.response.status === 400 &&
          error.response.data?.error?.includes("existe un jugador")
        ) {
          toast.error("Ya existe un jugador con ese nombre.");
        } else {
          toast.error("Error del servidor: " + error.response.data?.error);
        }
      } else {
        toast.error("Ocurrió un error al crear el jugador.");
      }
    }
  };

  // ========== EDIT JUGADOR ==========
  const handleEdit = (persona: RowObj) => {
    setEditPersona(persona);
    setNombre(persona.name[0]);
    setIsEditModalOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editPersona) return;

    if (!nombre.trim()) {
      toast.error("El nombre del jugador es obligatorio.");
      return;
    }
    const duplicate = data.some(
      (row) =>
        row.id !== editPersona.id &&
        row.name[0].trim().toLowerCase() === nombre.trim().toLowerCase()
    );
    if (duplicate) {
      toast.error("Ya existe un jugador con ese nombre.");
      return;
    }

    try {
      await axiosInstance.put(`/personas/${editPersona.id}`, {
        nombre,
      });
      setIsEditModalOpen(false);
      fetchPersonData();
    } catch (error: any) {
      console.error("Error editing persona:", error);
      if (axios.isAxiosError(error) && error.response) {
        if (
          error.response.status === 400 &&
          error.response.data?.error?.includes("existe un jugador")
        ) {
          toast.error("Ya existe un jugador con ese nombre.");
        } else {
          toast.error("Error del servidor: " + error.response.data?.error);
        }
      } else {
        toast.error("Ocurrió un error al editar el jugador.");
      }
    }
  };

  // ========== DELETE JUGADOR (SOFT DELETE) ==========
  const handleDelete = async () => {
    if (!deletePersonaId) return;
    try {
      // 1) Immediately remove from state so it disappears in the UI
      setData((prev) => prev.filter((p) => p.id !== deletePersonaId));

      // 2) Make the DELETE request (soft delete in backend)
      await axiosInstance.delete(`/personas/${deletePersonaId}`);

      // 3) Fire a custom event so JugadoresInactivos can instantly add it
      const deletedPlayerEvent = new CustomEvent("player-deleted", {
        detail: { id: deletePersonaId },
      });
      window.dispatchEvent(deletedPlayerEvent);

      // 4) Close the modal
      setIsDeleteModalOpen(false);
    } catch (error) {
      console.error("Error deleting persona:", error);
      toast.error("No se pudo eliminar al jugador.");
    }
  };

  // ====== TABLE CONFIG ======
  const columnHelper = createColumnHelper<RowObj>();
  const columns = [
    // NAME
    columnHelper.accessor("name", {
      id: "name",
      header: () => <span className={thLabelClass}>Nombre</span>,
      cell: (info) => (
        <div className="flex items-center gap-2">
          {isLoggedIn && (
            <button
              type="button"
              aria-label={`Editar ${info.getValue()[0]}`}
              onClick={() => handleEdit(info.row.original)}
              className="hidden h-6 w-6 shrink-0 items-center justify-center rounded-[6px] text-gray-600 transition-colors hover:bg-gray-100 hover:text-brand-500 dark:text-gray-500 dark:hover:bg-white/10 dark:hover:text-brand-200 sm:flex"
            >
              <MdEdit className="h-3.5 w-3.5" />
            </button>
          )}
          {isLoggedIn && (
            <button
              type="button"
              aria-label={`Eliminar ${info.getValue()[0]}`}
              onClick={() => {
                setDeletePersonaId(info.row.original.id);
                setIsDeleteModalOpen(true);
              }}
              className="flex h-6 w-6 shrink-0 items-center justify-center rounded-[6px] text-gray-600 transition-colors hover:bg-red-100 hover:text-red-600 dark:text-gray-500 dark:hover:bg-red-600/20 dark:hover:text-red-300 sm:hidden"
            >
              <IoTrash className="h-3.5 w-3.5" />
            </button>
          )}
          <span className="truncate font-semibold">{info.getValue()[0]}</span>
        </div>
      ),
    }),

    // ÚLTIMO EVENTO ASISTIDO
    columnHelper.accessor("date", {
      id: "date",
      header: () => <span className={thLabelClass}>Último</span>,
      cell: (info) => (
        <span className="tabular text-gray-700 dark:text-gray-300">
          {info.getValue()}
        </span>
      ),
    }),

    // BASE DE CÁLCULO
    columnHelper.accessor("baseCalculo", {
      id: "baseCalculo",
      header: () => <span className={thLabelClass}>Base de cálculo</span>,
      cell: (info) => (
        <span className="tabular text-gray-700 dark:text-gray-300">
          {info.getValue()}
        </span>
      ),
    }),

    // quantity
    columnHelper.accessor("quantity", {
      id: "quantity",
      header: () => (
        <span className={thLabelClass}>{`PJ ${new Date().getFullYear()}`}</span>
      ),
      cell: (info) => (
        <span className="tabular font-semibold">{info.getValue()}</span>
      ),
    }),

    // penas
    columnHelper.accessor("penas", {
      id: "penas",
      header: () => (
        <span className={thLabelClass}>{`Peñas ${new Date().getFullYear()}`}</span>
      ),
      cell: (info) => (
        <span className="tabular font-semibold">{info.getValue()}</span>
      ),
    }),

    // actions (delete)
    columnHelper.display({
      id: "actions",
      cell: (info) => (
        <div className="hidden justify-end sm:flex">
          {isLoggedIn && (
            <Button
              variant="destructive-soft"
              size="sm"
              onClick={() => {
                setDeletePersonaId(info.row.original.id);
                setIsDeleteModalOpen(true);
              }}
              disabled={!isLoggedIn}
            >
              Eliminar
            </Button>
          )}
        </div>
      ),
    }),
  ];

  // HOOK UP TABLE
  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      pagination,
    },
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  // ====== RENDER ======
  return (
    <div>
      {/* Button to open Create Jugador dropdown */}
      <div className="flex items-center justify-between gap-4 pb-5">
        <div className="text-left">
          <h2 className="text-[17px] font-semibold text-gray-900 dark:text-white">
            Activos
          </h2>
          <p className="mt-1 text-[13px] text-gray-700 dark:text-gray-400">
            {data.length} {data.length === 1 ? "jugador" : "jugadores"} en el plantel
          </p>
        </div>
        {isLoggedIn && <AgregarJugador />}
      </div>

      {/* Table */}
      <Card extra="select-none flex w-full flex-col border-none bg-transparent p-0 text-left shadow-none dark:!bg-transparent">
        {data.length === 0 ? (
          <EmptyState
            icon={<AiOutlineUser />}
            title="Todavía no hay jugadores activos"
            description={
              isLoggedIn
                ? "Agregá el primero para empezar a registrar asistencias."
                : "Cuando se cargue el plantel vas a verlo acá."
            }
            action={
              isLoggedIn ? (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => {
                    setIsCreateModalOpen(true);
                    setNuevoJugador("");
                  }}
                >
                  Agregar jugador
                </Button>
              ) : undefined
            }
          />
        ) : (
        <div className="-mx-1 w-full overflow-x-auto px-1">
          <table className="w-full sm:min-w-[420px]">
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id} className={theadRowClass}>
                  {headerGroup.headers.map((header) => {
                    const isUltimaAsistencia = header.column.id === "date";
                    const isActions = header.column.id === "actions";
                    return (
                      <th
                        key={header.id}
                        colSpan={header.colSpan}
                        onClick={header.column.getToggleSortingHandler()}
                        className={`${thClass}
                          ${
                            isLoggedIn && (isUltimaAsistencia || isActions)
                              ? "hidden sm:table-cell"
                              : ""
                          }
                          ${
                            !isLoggedIn && isUltimaAsistencia
                              ? "hidden sm:table-cell"
                              : ""
                          }
                          ${
                            !isLoggedIn && isActions
                              ? "hidden"
                              : ""
                          }
                        `}
                      >
                        <span className="inline-flex items-center gap-1">
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                          <SortIndicator direction={header.column.getIsSorted()} />
                        </span>
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id} className={trClass}>
                  {row.getVisibleCells().map((cell) => {
                    const isUltimaAsistencia = cell.column.id === "date";
                    const isActions = cell.column.id === "actions";
                    return (
                      <td
                        key={cell.id}
                        className={`${cell.column.id === "name" ? tdStrongClass : tdClass}
                          ${
                            isLoggedIn && (isUltimaAsistencia || isActions)
                              ? "hidden sm:table-cell"
                              : ""
                          }
                          ${
                            !isLoggedIn && isUltimaAsistencia
                              ? "hidden sm:table-cell"
                              : ""
                          }
                          ${
                            !isLoggedIn && isActions
                              ? "hidden"
                              : ""
                          }
                        `}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}

        {/* PAGINATION */}
        <Pagination
          className="mt-5 border-t border-gray-200/70 pt-5 dark:border-white/10"
          pageIndex={table.getState().pagination.pageIndex}
          pageCount={table.getPageCount()}
          canPreviousPage={table.getCanPreviousPage()}
          canNextPage={table.getCanNextPage()}
          onPreviousPage={() => table.previousPage()}
          onNextPage={() => table.nextPage()}
        />
      </Card>

      {/* CREATE PLAYER MODAL */}
      {isLoggedIn && (
        <Modal isOpen={isCreateModalOpen} onClose={() => setIsCreateModalOpen(false)}>
          <h3 className="select-none text-[17px] font-semibold text-gray-900 dark:text-white">
            Crear jugador
          </h3>
          <p className="mb-5 mt-1 select-none text-sm text-gray-700 dark:text-gray-400">
            Va a quedar disponible para marcar asistencias.
          </p>
          <TextField
            id="nuevo-jugador"
            label="Nombre"
            autoFocus
            value={nuevoJugador}
            onChange={(e) => setNuevoJugador(e.target.value)}
            placeholder="Introduce el nombre"
          />
          <div className="mt-6 flex select-none justify-end gap-2">
            <Button variant="secondary" onClick={() => setIsCreateModalOpen(false)}>
              Cancelar
            </Button>
            <Button variant="primary" onClick={handleCrearJugador}>
              Crear
            </Button>
          </div>
        </Modal>
      )}
      
      {/* EDIT MODAL */}
      {isLoggedIn && (
        <Modal isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)}>
          <h3 className="mb-5 select-none text-[17px] font-semibold text-gray-900 dark:text-white">
            Editar jugador
          </h3>
          <TextField
            id="nombre"
            label="Nombre"
            autoFocus
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            placeholder="Escribe un nombre"
          />
          <div className="mt-6 flex select-none justify-end gap-2">
            <Button variant="secondary" onClick={() => setIsEditModalOpen(false)}>
              Cancelar
            </Button>
            <Button variant="primary" onClick={handleSaveEdit}>
              Guardar cambios
            </Button>
          </div>
        </Modal>
      )}

      {/* DELETE CONFIRMATION MODAL */}
      {isLoggedIn && (
        <Modal isOpen={isDeleteModalOpen} onClose={() => setIsDeleteModalOpen(false)}>
          <h3 className="select-none text-[17px] font-semibold text-gray-900 dark:text-white">
            ¿Dar de baja al jugador?
          </h3>
          <p className="mt-2 select-none text-sm leading-relaxed text-gray-700 dark:text-gray-300">
            Esta acción hará descender al jugador. Vas a poder reactivarlo desde
            la lista de inactivos.
          </p>
          <div className="mt-6 flex select-none justify-end gap-2">
            <Button variant="secondary" onClick={() => setIsDeleteModalOpen(false)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Dar de baja
            </Button>
          </div>
        </Modal>
      )}
    </div>
  );
}

export default Jugadores;
